// Queries Repository Implementation
import { QueriesRepository } from "../../domain/repositories/QueriesRepository";
import {
  QueryEntity,
  CreateQueryParams,
  CreateSubQueryParams,
  QueryFilters,
  QueryListResult,
} from "../../domain/entities/QueryEntity";
import {
  QueryCommentEntity,
  CreateCommentParams,
} from "../../domain/entities/QueryCommentEntity";
import { QueryCountsEntity } from "../../domain/entities/QueryCountsEntity";
import { QueriesApi } from "../api/queriesApi";
import { QueryMapper, QueryCommentMapper, QueryCountsMapper } from "../mappers";
import { QueryListDataDTO, QueryPaginationDTO } from "../models/QueryDTO";

export class QueriesRepositoryImpl implements QueriesRepository {
  constructor(private api: QueriesApi) {}

  async create(params: CreateQueryParams): Promise<QueryEntity> {
    const response = await this.api.create({
      title: params.title,
      description: params.description,
      priority: params.priority,
      source: params.source,
      source_id: params.sourceId,
      assigned_to: params.assignedTo,
      review_required: params.reviewRequired ?? true,
    });

    return QueryMapper.toDomain(response.data);
  }

  async createSubQuery(params: CreateSubQueryParams): Promise<QueryEntity> {
    const response = await this.api.createSubQuery(params.parentQueryId, {
      title: params.title,
      description: params.description,
      priority: params.priority,
      source: params.source,
      source_id: params.sourceId,
      assigned_to: params.assignedTo,
      review_required: params.reviewRequired ?? true,
    });

    return QueryMapper.toDomain(response.data);
  }

  async update(
    queryId: number,
    updates: Partial<QueryEntity>
  ): Promise<QueryEntity> {
    const dto = QueryMapper.toDTO(updates);
    const response = await this.api.update(queryId, dto);
    return QueryMapper.toDomain(response.data);
  }

  async delete(queryId: number): Promise<void> {
    await this.api.delete(queryId);
  }

  async getDetails(queryId: number, clientView?: boolean): Promise<QueryEntity> {
    const response = await this.api.getDetails(queryId, clientView);
    return QueryMapper.toDomain(response.data);
  }

  async getAll(filters?: QueryFilters): Promise<QueryEntity[]> {
    const response = await this.api.getAll(filters);
    return response.data.map((dto) => QueryMapper.toDomain(dto));
  }

  async getQueriesBySource(
    source: string,
    sourceId?: number,
    filters?: QueryFilters,
    limit?: number,
    offset?: number
  ): Promise<QueryListResult> {
    const params = {
      ...(filters ?? {}),
      limit,
      offset,
    };
    const response = await this.api.getQueriesBySource(
      source,
      sourceId,
      params
    );
    return this.mapPaginatedResult(response.data);
  }

  async getAssignedToMe(
    userId: number,
    filters?: QueryFilters,
    limit?: number,
    offset?: number
  ): Promise<QueryListResult> {
    const params = {
      ...(filters ?? {}),
      limit,
      offset,
    };
    const response = await this.api.getAssignedToMe(userId, params);
    return this.mapPaginatedResult(response.data);
  }

  async getRaisedByMe(
    userId: number,
    filters?: QueryFilters,
    limit?: number,
    offset?: number
  ): Promise<QueryListResult> {
    const params = {
      ...(filters ?? {}),
      limit,
      offset,
    };
    const response = await this.api.getRaisedByMe(userId, params);
    return this.mapPaginatedResult(response.data);
  }

  async getSubQueries(queryId: number): Promise<QueryEntity[]> {
    const response = await this.api.getSubQueries(queryId);
    return response.data.map((dto) => QueryMapper.toDomain(dto));
  }

  async resolve(queryId: number): Promise<QueryEntity> {
    const response = await this.api.resolve(queryId);
    return QueryMapper.toDomain(response.data);
  }

  async addComment(params: CreateCommentParams): Promise<QueryCommentEntity> {
    const response = await this.api.addComment(params.queryId, {
      comment: params.comment,
    });
    return QueryCommentMapper.toDomain(response.data);
  }

  async getComments(queryId: number): Promise<QueryCommentEntity[]> {
    const response = await this.api.getComments(queryId);
    return response.data.map((dto) => QueryCommentMapper.toDomain(dto));
  }

  async getMyCounts(): Promise<QueryCountsEntity> {
    const response = await this.api.getMyCounts();
    return QueryCountsMapper.toDomain(response.data);
  }

  private mapPaginatedResult(data: QueryListDataDTO): QueryListResult {
    const pagination: QueryPaginationDTO = data.pagination ?? {
      limit: 5,
      offset: 0,
      has_more: false,
      total: 0,
    };

    return {
      queries: data.queries.map((dto) => QueryMapper.toDomain(dto)),
      pagination: {
        limit: pagination.limit,
        offset: pagination.offset,
        hasMore: pagination.has_more,
        total: pagination.total,
      },
      source: data.source
        ? {
            type: data.source.type,
            id: data.source.id,
          }
        : undefined,
    };
  }
}
